import { useTranslation } from 'react-i18next';
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { useCreateModuleMutation } from '@entities/Module';
import { cn } from '@shared/lib';
import { PlainField } from '@shared/ui';
import {
  LANGUAGE_LABELS,
  type Language,
  type Level,
  type ModuleSummary,
} from '@shared/types';

interface ModulePickerProps {
  value: string | null;
  modules: ModuleSummary[];
  locked: boolean;
  loading?: boolean;
  difficulty: Level;
  onChange: (id: string, created?: ModuleSummary) => void;
  /** lg — кнопка в строке заголовка «Новые карточки в [модуль]» (макет «Создание карточек»). */
  size?: 'md' | 'lg';
}

function Letter({ title, size = 'sm' }: { title: string; size?: 'sm' | 'md' | 'lg' }) {
  return (
    <span
      className={cn(
        'grid shrink-0 place-items-center bg-accent font-serif font-bold text-fg-inv leading-none',
        size === 'sm' && 'h-6 w-6 rounded-md text-[12px]',
        size === 'md' && 'h-7 w-7 rounded-md text-[12px]',
        size === 'lg' && 'h-7 w-7 rounded-[9px] text-[14px] font-semibold',
      )}
    >
      {(title || '?').charAt(0).toUpperCase()}
    </span>
  );
}

function ChevDown({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" className={className}>
      <path d="M4 6l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function Lock({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" className={className}>
      <rect x="3" y="7.5" width="10" height="6.5" rx="1.4" strokeLinejoin="round" />
      <path d="M5 7.5V5.2a3 3 0 1 1 6 0V7.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function Search({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" className={className}>
      <circle cx="7" cy="7" r="4.5" />
      <path d="M10.5 10.5L14 14" strokeLinecap="round" />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="M3 8.5l3 3 7-7" />
    </svg>
  );
}

// Временно: пока бэк/AI-пайплайн поддерживает только английский как source
// и русский как target. Остальные языки оставлены в enum'е на будущее.
const LANG_OPTIONS: ReadonlyArray<Language> = ['en', 'ru'] as const;
// const LANG_OPTIONS: ReadonlyArray<Language> = ['en', 'ru', 'de', 'fr', 'es', 'it'] as const;

function LangBadge({
  code,
  size = 'sm',
  tone = 'default',
}: {
  code: Language;
  size?: 'sm' | 'md';
  tone?: 'default' | 'accent';
}) {
  return (
    <span
      className={cn(
        'grid place-items-center rounded-md font-mono font-bold uppercase tracking-wide',
        size === 'sm' ? 'h-5 w-7 text-[12px]' : 'h-6 w-8 text-[12px]',
        tone === 'accent' ? 'bg-accent/15 text-accent' : 'bg-elev text-fg/90',
      )}
    >
      {code}
    </span>
  );
}

function LangSelect({
  value,
  exclude,
  onChange,
  label,
}: {
  value: Language;
  exclude?: Language;
  onChange: (lang: Language) => void;
  label: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  return (
    <div ref={ref} className="relative flex-1">
      <button
        type="button"
        aria-label={label}
        onClick={() => setOpen((o) => !o)}
        className={cn(
          'flex w-full items-center justify-between gap-1.5 rounded-md border border-line bg-card px-2 py-1.5 text-[14px] transition-colors',
          open ? 'border-accent' : 'hover:border-line-strong',
        )}
      >
        <span className="flex items-center gap-1.5">
          <LangBadge code={value} tone="accent" />
          <span className="truncate text-[12px] text-fg/85">{LANGUAGE_LABELS[value]}</span>
        </span>
        <ChevDown className={cn('h-3 w-3 text-muted transition-transform', open && 'rotate-180')} />
      </button>
      {open && (
        <div className="absolute left-0 right-0 top-[calc(100%+4px)] z-40 rounded-lg border border-line bg-[#1a1a1d] p-1 shadow-[0_16px_40px_-12px_rgba(0,0,0,0.6)]">
          {LANG_OPTIONS.map((l) => {
            const disabled = l === exclude;
            const active = l === value;
            return (
              <button
                key={l}
                type="button"
                disabled={disabled}
                onClick={() => {
                  if (disabled) return;
                  onChange(l);
                  setOpen(false);
                }}
                className={cn(
                  'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[14px] transition-colors',
                  active && 'bg-accent/10',
                  disabled
                    ? 'cursor-not-allowed opacity-30'
                    : !active && 'hover:bg-elev',
                )}
              >
                <LangBadge code={l} tone={active ? 'accent' : 'default'} />
                <span className={cn('truncate', active ? 'text-accent font-medium' : 'text-fg')}>
                  {LANGUAGE_LABELS[l]}
                </span>
                {active && <CheckIcon className="ml-auto h-3 w-3 text-accent" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Plus({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      className={className}
    >
      <path d="M8 3v10M3 8h10" />
    </svg>
  );
}

function Spinner({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 16 16" fill="none" className={cn('animate-orbit', className)}>
      <circle cx="8" cy="8" r="6" stroke="currentColor" strokeOpacity="0.25" strokeWidth="2" />
      <path
        d="M14 8a6 6 0 0 0-6-6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function ChipShellSkeleton({ size }: { size: 'md' | 'lg' }) {
  return (
    <div
      aria-busy
      aria-label="Загружаем модули"
      className={cn(
        'flex shrink-0 items-center gap-2 whitespace-nowrap rounded-pill text-muted',
        size === 'lg'
          ? 'h-10 border border-line bg-card pl-3 pr-3.5'
          : 'bg-elev py-1.5 pl-2.5 pr-3',
      )}
    >
      <Spinner className="h-3.5 w-3.5 text-accent" />
      <span className="text-[12px] font-medium leading-none">Загружаем модули…</span>
    </div>
  );
}

function ChipShell({
  module,
  locked,
  loading,
  onClick,
  expanded,
  size,
}: {
  module: ModuleSummary | null;
  locked: boolean;
  loading?: boolean;
  onClick?: () => void;
  expanded?: boolean;
  size: 'md' | 'lg';
}) {
  if (loading) return <ChipShellSkeleton size={size} />;

  const lg = size === 'lg';
  const base = cn(
    'flex min-w-0 max-w-full shrink-0 items-center gap-2 whitespace-nowrap rounded-pill transition-colors',
    // border-solid: базовое `button { border: 0 }` снимает стиль рамки.
    lg ? 'h-10 border border-solid border-line bg-card pl-1.5 pr-3' : 'bg-elev pl-1.5 pr-3 py-1.5',
  );
  const content = (
    <>
      <Letter title={module?.title ?? '?'} size={lg ? 'lg' : 'sm'} />
      <span
        className={cn(
          'min-w-0 truncate font-medium leading-none',
          lg ? 'max-w-[240px] text-[14px]' : 'max-w-[180px] text-[14px]',
        )}
      >
        {module?.title ?? 'Выбрать модуль'}
      </span>
      {module && (
        <span className={cn('shrink-0 text-muted leading-none', lg ? 'text-[14px]' : 'text-[12px]')}>
          · {module.words}
        </span>
      )}
      {locked ? (
        <span
          title="Модуль зафиксирован"
          className="ml-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-card text-muted"
        >
          <Lock className="h-2.5 w-2.5" />
        </span>
      ) : (
        <ChevDown className={cn('ml-0.5', lg ? 'h-3.5 w-3.5 text-muted' : 'h-3 w-3 text-muted-2')} />
      )}
    </>
  );
  if (locked || !onClick) {
    return <div className={cn(base, 'cursor-default')}>{content}</div>;
  }
  return (
    <button
      type="button"
      onClick={onClick}
      aria-expanded={expanded}
      className={cn(base, lg ? 'hover:bg-[#24242a]' : 'hover:bg-[#2d2d33]')}
    >
      {content}
    </button>
  );
}

export function ModulePicker({
  value,
  modules,
  locked,
  loading,
  difficulty,
  onChange,
  size = 'md',
}: ModulePickerProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  // Описание задаёт тему примеров карточек (2026-10-06, владелец): без него тема — по названию.
  const [newDesc, setNewDesc] = useState('');
  const [newSourceLang, setNewSourceLang] = useState<Language>('en');
  const [newTargetLang, setNewTargetLang] = useState<Language>('ru');
  const [hover, setHover] = useState(0);
  const wrapRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLDivElement>(null);
  const createRef = useRef<HTMLDivElement>(null);

  const createMut = useCreateModuleMutation();
  const current = modules.find((m) => m.id === value) ?? null;

  useEffect(() => {
    if (!open) return;
    const onDocMouse = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onDocKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocMouse);
    document.addEventListener('keydown', onDocKey);
    return () => {
      document.removeEventListener('mousedown', onDocMouse);
      document.removeEventListener('keydown', onDocKey);
    };
  }, [open]);

  useEffect(() => {
    if (open) {
      const id = window.setTimeout(() => searchRef.current?.focus(), 30);
      return () => window.clearTimeout(id);
    }
  }, [open]);

  useEffect(() => {
    if (creating) {
      const id = window.setTimeout(() => createRef.current?.focus(), 30);
      return () => window.clearTimeout(id);
    }
  }, [creating]);

  useEffect(() => {
    setHover(0);
  }, [query]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return modules;
    return modules.filter((m) => m.title.toLowerCase().includes(q));
  }, [modules, query]);

  const choose = (m: ModuleSummary) => {
    onChange(m.id);
    setOpen(false);
    setQuery('');
  };

  const submitNew = async () => {
    const name = newName.trim();
    if (!name || createMut.isPending) return;
    if (newSourceLang === newTargetLang) return;
    try {
      const created = await createMut.mutateAsync({
        name,
        desc: newDesc,
        cefrLevel: difficulty,
        sourceLang: newSourceLang,
        targetLang: newTargetLang,
      });
      onChange(created.id, created);
      setOpen(false);
      setCreating(false);
      setNewName('');
      setNewDesc('');
      setQuery('');
    } catch {
      // ошибка попадает в createMut.error, отображается под инпутом
    }
  };

  const onListKey = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (creating) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHover((h) => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHover((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter' && filtered[hover]) {
      e.preventDefault();
      choose(filtered[hover]);
    }
  };

  const showSearch = modules.length > 4;

  return (
    <div ref={wrapRef} className="relative">
      <ChipShell
        module={current}
        locked={locked}
        loading={loading}
        expanded={open}
        size={size}
        onClick={loading ? undefined : () => setOpen((o) => !o)}
      />
      {open && !locked && !loading && (
        <div
          className={cn(
            'animate-rise absolute left-0 z-50 w-[340px] rounded-2xl border border-line bg-[#1a1a1d] shadow-[0_24px_64px_-12px_rgba(0,0,0,0.6)]',
            size === 'lg' ? 'top-12' : 'top-[44px]',
          )}
          onKeyDown={onListKey}
        >
          {showSearch && (
            <div className="border-b border-line p-2">
              <label className="relative block">
                <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted" />
                <PlainField
                  ref={searchRef}
                  value={query}
                  onChange={setQuery}
                  placeholder="Найти модуль…"
                  className="w-full rounded-lg bg-elev py-2 pl-8 pr-3 text-[14px] outline-none placeholder:text-muted-2"
                  ariaLabel="Найти модуль"
                />
              </label>
            </div>
          )}

          <div className="cw-nice-scroll max-h-[280px] overflow-y-auto p-1.5">
            {filtered.length === 0 ? (
              <div className="px-3 py-4 text-center text-[12px] text-muted">
                {modules.length === 0
                  ? 'У тебя пока нет модулей — создай первый ниже.'
                  : `Ничего не нашли по «${query}»`}
              </div>
            ) : (
              filtered.map((m, i) => {
                const isCurrent = m.id === value;
                const isHover = i === hover;
                return (
                  <button
                    key={m.id}
                    type="button"
                    onMouseEnter={() => setHover(i)}
                    onClick={() => choose(m)}
                    className={cn(
                      'flex w-full items-center gap-2.5 rounded-lg px-2 py-2 text-left transition-colors',
                      isHover && 'bg-elev',
                    )}
                  >
                    <Letter title={m.title} size="md" />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-[14px] font-medium text-fg">{m.title}</div>
                      <div className="text-[12px] text-muted">
                        {m.words} слов{m.state ? ` · ${m.state === 'new' ? 'Новый' : 'В прогрессе'}` : ''}
                      </div>
                    </div>
                    {isCurrent && <CheckIcon className="h-3.5 w-3.5 text-accent" />}
                  </button>
                );
              })
            )}
          </div>

          <div className="border-t border-line p-1.5">
            {!creating ? (
              <button
                type="button"
                onClick={() => setCreating(true)}
                className="flex w-full items-center gap-2.5 rounded-lg px-2 py-2 text-left text-[14px] font-medium text-accent transition-colors hover:bg-accent/10"
              >
                <span className="grid h-7 w-7 place-items-center rounded-md border border-dashed border-accent/40 text-accent">
                  <Plus className="h-3.5 w-3.5" />
                </span>
                Создать новый модуль
              </button>
            ) : (
              <div className="flex flex-col gap-2 rounded-lg border border-dashed border-accent bg-elev/60 p-2">
                <div className="text-[12px] font-semibold uppercase tracking-[0.14em] text-muted">
                  Новый модуль
                </div>
                <PlainField
                  ref={createRef}
                  value={newName}
                  onChange={setNewName}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      void submitNew();
                    } else if (e.key === 'Escape') {
                      setCreating(false);
                      setNewName('');
                    }
                  }}
                  placeholder="например, Travel & Transport"
                  className="w-full rounded-md border border-line bg-card px-3 py-2 text-[14px] outline-none placeholder:text-muted-2 focus:border-accent"
                  ariaLabel="Название нового модуля"
                />
                <div>
                  <div className="mb-1 flex items-baseline gap-1.5 text-[12px] font-semibold uppercase tracking-[0.14em] text-muted">
                    {t('cardsWorkspace.picker.create.descLabel')}
                    <span className="font-medium normal-case tracking-normal text-muted-2">
                      {t('cardsWorkspace.picker.create.descOptional')}
                    </span>
                  </div>
                  <textarea
                    value={newDesc}
                    onChange={(e) => setNewDesc(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        void submitNew();
                      } else if (e.key === 'Escape') {
                        setCreating(false);
                        setNewName('');
                        setNewDesc('');
                      }
                    }}
                    rows={2}
                    maxLength={2000}
                    placeholder={t('cardsWorkspace.picker.create.descPlaceholder')}
                    aria-label={t('cardsWorkspace.picker.create.descLabel')}
                    className="block w-full resize-none rounded-md border border-line bg-card px-3 py-2 text-[14px] leading-snug outline-none placeholder:text-muted-2 focus:border-accent"
                  />
                  <div className="mt-1 text-[12px] leading-snug text-muted">
                    {t('cardsWorkspace.picker.create.descHint')}
                  </div>
                </div>
                {/* Языковая пара: изучаю → переводы */}
                <div>
                  <div className="mb-1 text-[12px] font-semibold uppercase tracking-[0.14em] text-muted">
                    Языковая пара
                  </div>
                  <div className="flex items-center gap-1.5">
                    <LangSelect
                      label="Исходный язык"
                      value={newSourceLang}
                      exclude={newTargetLang}
                      onChange={setNewSourceLang}
                    />
                    <span aria-hidden className="text-[12px] text-muted-2">→</span>
                    <LangSelect
                      label="Язык переводов"
                      value={newTargetLang}
                      exclude={newSourceLang}
                      onChange={setNewTargetLang}
                    />
                  </div>
                  <div className="mt-1 text-[12px] leading-snug text-muted">
                    Слово на <b className="text-fg">{LANGUAGE_LABELS[newSourceLang]}</b> → перевод
                    на <b className="text-fg">{LANGUAGE_LABELS[newTargetLang]}</b>
                  </div>
                </div>
                {createMut.error && (
                  <div className="text-[12px] text-danger">
                    Не получилось создать модуль. Попробуй другое название.
                  </div>
                )}
                <div className="flex items-center justify-end gap-1.5">
                  <button
                    type="button"
                    onClick={() => {
                      setCreating(false);
                      setNewName('');
                      setNewDesc('');
                    }}
                    className="rounded-pill px-3 py-1.5 text-[12px] font-medium text-muted hover:text-fg"
                  >
                    Отмена
                  </button>
                  <button
                    type="button"
                    onClick={() => void submitNew()}
                    disabled={!newName.trim() || createMut.isPending}
                    className="rounded-pill bg-accent px-3 py-1.5 text-[12px] font-semibold text-fg-inv disabled:opacity-40"
                  >
                    {createMut.isPending ? '…' : 'Создать и выбрать'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
